import { useAllGodowns } from "http-hooks/godown/useGodown";
import DeleteGodown from "components/godown/DeleteGodown";
import { useState } from "react";
import Modal from "components/ui/modals";
import useModalStore from "store/modals";
import {
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
  LoadingRow,
  EmptyRow,
  ActionButton,
  Badge,
} from "components/ui/Table";
import InputSearch from "components/ui/InputSearch";
import Select from "components/ui/Select";
import Pagination from "components/ui/Pagination";
import {
  PencilSquareIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

// Constants moved to top to prevent re-renders
const statusOptions = [
  { value: "", label: "All Status" },
  { value: "1", label: "Active" },
  { value: "0", label: "Inactive" },
];

const initialFilter = {
  name: "",
  agstat: "",
  page: 1,
  limit: 10,
};

export default function CompanyGodowns() {
  const [deleteGodownId, setDeleteGodownId] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [filter, setFilter] = useState(initialFilter);
  const { openModal } = useModalStore();

  const openSetModal = (id) => {
    openModal('godown', id);
  };

  const handleSearch = (searchTerm) => {
    setFilter((prev) => ({ ...prev, name: searchTerm, page: 1 }));
  };

  const handleStatusChange = (selectedOption) => {
    setSelectedStatus(selectedOption);
    setFilter((prev) => ({
      ...prev,
      agstat: selectedOption?.value || "",
      page: 1,
    }));
  };

  const clearFilters = () => {
    setSelectedStatus(null);
    setFilter(initialFilter);
  };

  const handlePageChange = (page) => {
    setFilter((prev) => ({ ...prev, page }));
  };

  const {
    isLoading: isAllGodownsLoading,
    data: { data: allgodowns, page: currentPage, totalPages } = [],
  } = useAllGodowns(filter);

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {deleteGodownId && (
        <Modal open={!!deleteGodownId} setOpen={(val) => !val && setDeleteGodownId(null)}>
          <DeleteGodown
            id={deleteGodownId}
            onClose={() => setDeleteGodownId(null)}
          />
        </Modal>
      )}

      {/* Header */}
      <div className="sm:flex sm:items-center mb-6">
        <div className="sm:flex-auto">
          <h1 className="font-semibold leading-6 text-3xl text-gray-900">
            Godown Management
          </h1>
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
          <button
            type="button"
            onClick={() => openSetModal()}
            className="block rounded-md bg-gray-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600"
          >
            Add Godown
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <div className="bg-white rounded-lg p-3">
          <InputSearch
            onSearch={handleSearch}
            label="Godown Name"
            placeholder="Search by name"
          />
        </div>

        <div className="bg-white rounded-lg p-3">
          <Select
            options={statusOptions}
            value={selectedStatus}
            onChange={handleStatusChange}
            label="Status"
            placeholder="Select status"
            isClearable={true}
            isSearchable={false}
          />
        </div>

        <div className="bg-white rounded-lg p-3 flex items-end">
          <button
            type="button"
            onClick={clearFilters}
            className="w-full rounded-md bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-300"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="mt-8 flow-root">
        <Table>
          <THead>
            <TR>
              <TH first>Godown Name</TH>
              <TH>Address</TH>
              <TH>State</TH>
              <TH>Type</TH>
              <TH>Status</TH>
              <TH last>
                <span className="sr-only">Actions</span>
              </TH>
            </TR>
          </THead>
          <TBody>
            {isAllGodownsLoading ? (
              <LoadingRow colSpan={6} message="Loading godowns..." />
            ) : allgodowns?.length === 0 ? (
              <EmptyRow colSpan={6} message="No godowns found." />
            ) : (
              allgodowns?.map((godown) => (
                <TR key={godown.id}>
                  <TD first>{godown.name}</TD>
                  <TD>{godown.address}</TD>
                  <TD>{godown.state}</TD>
                  <TD>
                    {godown.systemg === 1 ? (
                      <Badge variant="primary">System</Badge>
                    ) : (
                      <Badge variant="default">User</Badge>
                    )}
                  </TD>
                  <TD>
                    {godown.agstat === 1 ? (
                      <CheckIcon className="h-5 w-5 text-green-500" aria-hidden="true" />
                    ) : (
                      <XMarkIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
                    )}
                  </TD>
                  <TD last>
                    {godown.systemg !== 1 && (
                      <>
                        <ActionButton
                          variant="secondary"
                          onClick={() => openSetModal(godown.id)}
                          title="Edit Godown"
                        >
                          <PencilSquareIcon className="h-4 w-4" aria-hidden="true" />
                          <span className="sr-only">Edit godown, {godown.name}</span>
                        </ActionButton>
                        <ActionButton
                          variant="danger"
                          onClick={() => setDeleteGodownId(godown.id)}
                          title="Delete Godown"
                          className="mr-0"
                        >
                          <TrashIcon className="h-4 w-4" aria-hidden="true" />
                          <span className="sr-only">Delete godown, {godown.name}</span>
                        </ActionButton>
                      </>
                    )}
                    {godown.systemg === 1 && (
                      <span className="text-sm text-gray-500 italic">Protected</span>
                    )}
                  </TD>
                </TR>
              ))
            )}
          </TBody>
        </Table>
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        showPageJump={true}
      />
    </div>
  );
}

